import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useContact } from '../contexts/ContactContext';
import buildingImg from '../assets/building.png';
import habeebImg from '../assets/habeeb.png';

const About = () => {
  const { contactInfo } = useContact();

  return (
    <div className="bg-gray-50 min-h-screen w-full font-sans">
      <SEO 
        title="About Us | HBB0011 REALTY"
        description="Learn about the visionary team at HBB0011 REALTY. Discover our legacy of trust, excellence, and our commitment to redefining luxury real estate."
        keywords="about HBB0011 REALTY, luxury real estate team, corporate architecture vision, real estate excellence"
      />
      
      {/* 1. Hero Section */}
      <section className="relative w-full pt-32 pb-40 md:pt-40 md:pb-48 bg-[#041424] overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={buildingImg} 
            alt="Building background" 
            className="w-full h-full object-cover opacity-40 mix-blend-luminosity"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm font-semibold tracking-widest">
            <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
            <span className="text-gray-400">&gt;</span>
            <span className="text-[#D4AF37] uppercase">About Us</span>
          </div>

          {/* Heading */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight">
            ABOUT US
          </h1>
          
          <div className="w-24 h-1 bg-[#D4AF37] mb-6"></div>

          <h2 className="text-2xl md:text-3xl text-[#D4AF37] font-medium mb-6">
            Building Trust. Creating Landmarks.
          </h2>

          <p className="text-gray-300 max-w-2xl text-lg leading-relaxed">
            HBB0011 Realty Realtors Pvt. Ltd. is a premium real estate
            company delivering excellence in residential, commercial,
            and investment properties across India.
          </p>
        </div>
      </section>

      {/* 2. Stats Section (Overlapping Hero) */}
      <section className="relative z-20 max-w-7xl mx-auto px-6 -mt-16">
        <div className="bg-[#041424] border-t-4 border-[#D4AF37] rounded-md shadow-2xl py-8 px-4 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-gray-700/50">
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left px-4">
              <span className="material-symbols-outlined text-4xl text-[#D4AF37]">workspace_premium</span>
              <div>
                <h3 className="text-3xl font-bold text-white">1+</h3>
                <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">Years of Experience</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left px-4">
              <span className="material-symbols-outlined text-4xl text-[#D4AF37]">domain</span>
              <div>
                <h3 className="text-3xl font-bold text-white">1+</h3>
                <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">Projects Delivered</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left px-4">
              <span className="material-symbols-outlined text-4xl text-[#D4AF37]">groups</span>
              <div>
                <h3 className="text-3xl font-bold text-white">10+</h3>
                <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">Customers</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left px-4">
              <span className="material-symbols-outlined text-4xl text-[#D4AF37]">location_on</span>
              <div>
                <h3 className="text-3xl font-bold text-white">5+</h3>
                <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">Locations</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text */}
          <div className="lg:col-span-4">
            <h4 className="text-[#D4AF37] uppercase text-sm font-bold tracking-widest mb-3">Our Story</h4>
            <h2 className="text-4xl text-[#041424] font-light mb-6 leading-tight">
              A Vision Built on <br />
              <span className="font-bold">Trust and Excellence</span>
            </h2>
            <div className="space-y-4 text-gray-600 text-sm leading-relaxed mb-8">
              <p>
                HBB0011 Realty Realtors Pvt. Ltd. was founded with a mission to redefine real estate by building trust and creating long-term value. We deliver premium residential, commercial, and investment properties with transparency, quality, and commitment.
              </p>
              <p>
                From concept to completion, we ensure excellence in every detail, making us a trusted partner for homebuyers, investors, and businesses across India.
              </p>
            </div>
            <button className="bg-[#041424] text-white text-xs font-bold uppercase tracking-wider px-6 py-4 flex items-center gap-3 hover:bg-[#062038] transition-colors rounded-sm">
              Download Company Profile
              <span className="material-symbols-outlined text-lg">download</span>
            </button>
          </div>

          {/* Center Image */}
          <div className="lg:col-span-4 flex justify-center lg:px-4">
            <div className="w-full max-w-sm relative rounded-2xl overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.4)] transition-transform duration-700 hover:-translate-y-2 group">
              {/* Glassy 3D edge effect */}
              <div className="absolute inset-0 z-10 pointer-events-none rounded-2xl ring-1 ring-inset ring-black/10 shadow-[inset_0_2px_20px_rgba(255,255,255,0.8),inset_0_-4px_20px_rgba(0,0,0,0.1)]"></div>
              
              <img 
                src={habeebImg} 
                alt="Habeeb Rahiman M" 
                className="w-full h-full object-cover object-bottom relative z-0 scale-100 transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </div>
          </div>

          {/* Right Text */}
          <div className="lg:col-span-4 pl-0 lg:pl-8">
            <h4 className="text-[#D4AF37] uppercase text-sm font-bold tracking-widest mb-2">Founder & CEO</h4>
            <h3 className="text-3xl text-[#041424] font-bold mb-6">Habeeb Rahiman M</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-8">
              With a vision to transform real estate, Mr. Habeeb Rahiman M leads HBB0011 Realty Realtors Pvt. Ltd. with integrity, innovation, and a customer-first approach. His commitment to excellence and dedication to creating value drives the company towards building landmarks that define the future.
            </p>
            
            {/* Signature Font */}
            <div className="mt-8">
              <div className="inline-block relative mb-2">
                <span className="text-xl sm:text-2xl font-sans font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-yellow-400 uppercase tracking-widest relative z-10">HABEEB RAHIMAN M</span>
                <div className="absolute -bottom-1 left-0 w-1/3 h-[2px] bg-[#D4AF37]"></div>
              </div>
              <p className="text-sm text-gray-500 font-bold uppercase tracking-widest">Founder & CEO</p>
            </div>
          </div>

        </div>
      </section>

      {/* 4. Vision & Mission Cards */}
      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Vision */}
          <div className="bg-[#041424] rounded-xl p-10 relative overflow-hidden group flex items-start gap-6 shadow-xl">
            <img src={buildingImg} className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-screen transition-transform duration-700 group-hover:scale-105" alt="bg" />
            <div className="relative z-10 flex-shrink-0">
              <div className="w-14 h-14 rounded-full border border-[#D4AF37]/50 flex items-center justify-center">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl">visibility</span>
              </div>
            </div>
            <div className="relative z-10">
              <h3 className="text-[#D4AF37] text-2xl font-bold mb-3 uppercase tracking-wider">Our Vision</h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                To be a trusted real estate brand known for quality, transparency, and customer satisfaction across India.
              </p>
              <Link to="/about" className="text-white text-xs font-bold tracking-widest flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
                VIEW MORE <span className="material-symbols-outlined text-base">arrow_forward</span>
              </Link>
            </div>
          </div>

          {/* Mission */}
          <div className="bg-[#041424] rounded-xl p-10 relative overflow-hidden group flex items-start gap-6 shadow-xl">
            <img src={buildingImg} className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-screen transition-transform duration-700 group-hover:scale-105" alt="bg" />
            <div className="relative z-10 flex-shrink-0">
              <div className="w-14 h-14 rounded-full border border-[#D4AF37]/50 flex items-center justify-center">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl">track_changes</span>
              </div>
            </div>
            <div className="relative z-10">
              <h3 className="text-[#D4AF37] text-2xl font-bold mb-3 uppercase tracking-wider">Our Mission</h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                To deliver exceptional real estate solutions that create long-term value for our clients and communities through innovation, integrity, and excellence.
              </p>
              <Link to="/about" className="text-white text-xs font-bold tracking-widest flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
                VIEW MORE <span className="material-symbols-outlined text-base">arrow_forward</span>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* 5. Core Values */}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          
          <h2 className="text-3xl font-bold text-[#041424] uppercase tracking-widest mb-4">Our Core Values</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mb-16"></div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            
            <div className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-5xl text-[#041424] mb-4 font-light">verified_user</span>
              <h4 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-2">Trust</h4>
              <p className="text-gray-500 text-xs leading-relaxed">We build lasting relationships through honesty and integrity.</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-5xl text-[#041424] mb-4 font-light">star</span>
              <h4 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-2">Excellence</h4>
              <p className="text-gray-500 text-xs leading-relaxed">We are committed to delivering the highest quality in everything we do.</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-5xl text-[#041424] mb-4 font-light">support_agent</span>
              <h4 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-2">Customer Focus</h4>
              <p className="text-gray-500 text-xs leading-relaxed">Our clients are at the heart of every decision we make.</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-5xl text-[#041424] mb-4 font-light">lightbulb</span>
              <h4 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-2">Innovation</h4>
              <p className="text-gray-500 text-xs leading-relaxed">We embrace new ideas and modern solutions to create better experiences.</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-5xl text-[#041424] mb-4 font-light">handshake</span>
              <h4 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-2">Integrity</h4>
              <p className="text-gray-500 text-xs leading-relaxed">We conduct business with fairness, transparency, and accountability.</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-5xl text-[#041424] mb-4 font-light">trending_up</span>
              <h4 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-2">Growth</h4>
              <p className="text-gray-500 text-xs leading-relaxed">We are driven to create sustainable value for our clients and stakeholders.</p>
            </div>

          </div>
        </div>
      </section>

      {/* 6. Call to Action */}
      <section className="bg-[#041424] py-20 relative overflow-hidden">
        {/* Subtle right-side graphic */}
        <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none translate-x-1/4 translate-y-1/4">
          <span className="material-symbols-outlined text-[300px] text-white">location_city</span>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left CTA Text */}
          <div>
            <h4 className="text-[#D4AF37] uppercase text-xs font-bold tracking-widest mb-4">Let's Build the Future Together</h4>
            <h2 className="text-4xl md:text-5xl text-white font-light mb-6 leading-tight">
              Your Trust. <span className="font-bold">Our Commitment.</span>
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-md">
              Partner with HBB0011 Realty Realtors Pvt. Ltd. and experience real estate solutions that promise trust, quality, and long-term value.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#041424] px-8 py-4 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-white transition-colors duration-300">
              Get in Touch <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </Link>
          </div>

          {/* Right Contact Info */}
          <div className="flex flex-col gap-6 lg:pl-16">
            <div className="flex items-center gap-4">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl">call</span>
              <p className="text-white text-sm tracking-wide">{contactInfo?.phone}</p>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl">mail</span>
              <p className="text-white text-sm tracking-wide">{contactInfo?.email}</p>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl">language</span>
              <p className="text-white text-sm tracking-wide">www.hbb0011realty.com</p>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default About;